/**
 * Local sample prospect / farm-system data (fixtures mode).
 *
 * A deterministic pool of ranked prospects, each owned by one of the 10 sample
 * fantasy teams (team-1 is the signed-in user), with per-source rankings
 * (MLB.com / Baseball America / Baseball Prospectus). Drives the Farm page:
 * the user's prospects, a cumulative team farm-system ranking, and the overall
 * top-prospect board — all without the API.
 */

import { getSampleMinorLeagueStats } from './playersFixtures';

const FIRST = [
  'Jasper', 'Malik', 'Ren', 'Cade', 'Nico', 'Bo', 'Kai', 'Tariq', 'Emil', 'Jax',
  'Dev', 'Rylan', ' Favio', 'Cruz', 'Ledger', 'Odin', 'Sur', 'Kip', 'Zeb', 'Ames',
  'Marcelo', 'Hyun-woo', 'Deshawn', 'Ari', 'Bennett', 'Colton', 'Dario', 'Ezequiel', 'Finn', 'Gustavo',
  'Hiro', 'Ismael', 'Jamal', 'Kwame', 'Lorenzo', 'Matthias', 'Nolan', 'Osvaldo', 'Pierce', 'Quincy',
].map((s) => s.trim());
const LAST = [
  'Aoki', 'Barros', 'Chen', 'Delacroix', 'Eriksen', 'Fontaine', 'Griffith', 'Haddad', 'Iverson', 'Jimenez',
  'Kobayashi', 'Lund', 'Maldonado', 'Nakashima', 'Oduya', 'Priet', 'Quan', 'Rasmussen', 'Solano', 'Trevino',
  'Ueno', 'Villalobos', 'Wagner', 'Xiong', 'Yoon', 'Zabala', 'Amaya', 'Bergstrom', 'Cabrera', 'Dieng',
  'Ellison', 'Fuentes', 'Ghirardi', 'Halvorsen', 'Ito', 'Jovanovic', 'Kearns', 'Larosa', 'Muños', 'Nieves',
];
const ORGS = [
  'NYY', 'BOS', 'TB', 'TOR', 'BAL', 'CLE', 'DET', 'KC', 'MIN', 'CWS',
  'HOU', 'LAA', 'OAK', 'SEA', 'TEX', 'ATL', 'MIA', 'NYM', 'PHI', 'WSH',
  'CHC', 'CIN', 'MIL', 'PIT', 'STL', 'ARI', 'COL', 'LAD', 'SD', 'SF',
];
const POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'OF', 'RHP', 'LHP'];
const LEVELS = ['A', 'A+', 'AA', 'AAA'];
export const PROSPECT_SOURCES = ['MLB.com', 'Baseball America', 'Baseball Prospectus'];

// Mirrors leagueFixtures SAMPLE_TEAMS order; team-1 is the signed-in user's team.
const FANTASY_TEAMS = [
  'Turner Tomahawks', 'Double Down', 'Cascade Kings', 'Motor City Maulers', 'Harbor Hounds',
  'Summit Serpents', 'Delta Dynamos', 'Ironclad Owls', 'Prairie Pioneers', 'Coastal Cyclones',
];

function rand(seed) {
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

// String-seeded RNG so a prospect's per-period line is stable across renders.
function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function srand(s) {
  const x = Math.sin(hashStr(s) + 1) * 10000;
  return x - Math.floor(x);
}
// Round a fractional count up or down deterministically (r in [0,1)); lets a
// tiny expected value like 0.12 HR read as "usually 0, occasionally 1".
const probRound = (x, r) => Math.floor(x) + (r < x - Math.floor(x) ? 1 : 0);
const fmtAvg = (n) => n.toFixed(3).replace(/^0/, '');

/**
 * Time-period windows for the Farm stat board. `g` ≈ the player's team-games in
 * the window; `noise` is the rate-stat wobble (small samples swing more). The
 * current-season windows (Today…YTD) describe the in-progress 2026 season; 2025
 * is last year's completed line, so it carries the fullest counting totals.
 */
const SEASON_G = 140; // games behind the full-season "2025" anchor
export const FARM_PERIODS = [
  { id: 'today', label: 'Today', g: 1, noise: 0.85 },
  { id: 'yesterday', label: 'Yesterday', g: 1, noise: 0.85 },
  { id: 'l7', label: 'L7', g: 6, noise: 0.42 },
  { id: 'l30', label: 'L30', g: 26, noise: 0.22 },
  { id: 'ytd', label: 'YTD', g: 108, noise: 0.07 },
  { id: '2025', label: '2025', g: SEASON_G, noise: 0.03 },
];
const seasonOf = (period) => (period.id === '2025' ? 2025 : 2026);

// A hitter's line for one window. Counting stats scale down from the full-season
// anchor by games; AVG/OPS hover around the anchor rate with per-window noise.
function genHitterLine(anchor, period, k) {
  if (period.g === 1 && srand(k + 'dnp') < 0.14) { // sat out this day
    return { season: seasonOf(period), G: 0, AB: 0, H: 0, AVG: '—', HR: '—', RBI: '—', R: '—', SB: '—', OPS: '—', dnp: true };
  }
  const na = (s) => (srand(k + s) * 2 - 1) * period.noise;
  const AB = Math.max(1, Math.round((3.8 + srand(k + 'pg') * 0.5) * period.g));
  const wAvg = clamp((parseFloat(anchor.AVG) || 0.26) * (1 + na('avg')), 0.05, 0.55);
  const H = Math.min(AB, probRound(AB * wAvg, srand(k + 'h')));
  const share = period.g / SEASON_G;
  const cnt = (key, s) => probRound(anchor[key] * share * (1 + na(s)), srand(k + s + 'p'));
  const HR = cnt('HR', 'hr');
  // Derive OPS from this window's own line so it can't contradict AVG: a hitless
  // game reads as a low OPS, a multi-hit or homer game as a high one.
  const TB = Math.round((H - HR) * (1.3 + srand(k + 'xb') * 0.25) + HR * 4);
  const BB = probRound(AB * 0.09 * (1 + na('bb')), srand(k + 'bb'));
  return {
    season: seasonOf(period), G: period.g, AB, H,
    AVG: fmtAvg(H / AB),
    HR, RBI: cnt('RBI', 'rbi'), R: cnt('R', 'r'), SB: cnt('SB', 'sb'),
    OPS: fmtAvg(clamp((H + BB) / Math.max(1, AB + BB) + TB / AB, 0, 2.5)),
  };
}

// A pitcher's line for one window. Modeled by appearances: starters go ~every 5
// team-games for ~5 IP, relievers ~every 2.4 for ~1.2 IP, so on a single day most
// don't pitch at all (empty line). Rate stats wobble around the anchor.
function genPitcherLine(anchor, period, k, id) {
  const isStarter = srand(id + 'role') < 0.58;
  const expApp = period.g / (isStarter ? 5 : 2.4);
  const apps = period.g === 1 ? (srand(k + 'app') < expApp ? 1 : 0) : probRound(expApp, srand(k + 'app'));
  if (apps === 0) { // did not pitch in this window
    return { season: seasonOf(period), W: '—', L: '—', SV: '—', ERA: '—', WHIP: '—', K: '—', IP: '—', dnp: true };
  }
  const na = (s) => (srand(k + s) * 2 - 1) * period.noise;
  const IP = Math.round(apps * (isStarter ? 5.3 : 1.2) * (1 + na('ip') * 0.4) * 10) / 10;
  const isCloser = !isStarter && srand(id + 'cl') < 0.33;
  return {
    season: seasonOf(period), IP,
    W: probRound(apps * (isStarter ? 0.4 : 0.12) * (1 + na('w')), srand(k + 'wp')),
    L: probRound(apps * 0.3, srand(k + 'lp')),
    SV: isCloser ? probRound(apps * 0.55, srand(k + 'svp')) : (!isStarter ? probRound(apps * 0.06, srand(k + 'svp')) : 0),
    ERA: clamp(parseFloat(anchor.ERA) * (1 + na('era')), 0, 13.5).toFixed(2),
    WHIP: clamp(parseFloat(anchor.WHIP) * (1 + na('whip')), 0.3, 3).toFixed(2),
    K: probRound((anchor.K / Math.max(1, anchor.IP)) * IP * (1 + na('k')), srand(k + 'kp')),
  };
}

let POOL = null;
function buildPool() {
  const N = 60;
  const list = [];
  for (let i = 0; i < N; i++) {
    const base = i + 1; // seed the source ranks around this so the list is coherent
    const mlb = clamp(base + Math.round((rand(i * 1.7 + 1) - 0.5) * 10), 1, 80);
    const ba = clamp(base + Math.round((rand(i * 2.9 + 2) - 0.5) * 10), 1, 80);
    const bp = clamp(base + Math.round((rand(i * 4.3 + 3) - 0.5) * 10), 1, 80);
    const avgRank = (mlb + ba + bp) / 3;
    const teamIdx = Math.floor(rand(i * 5.1 + 4) * FANTASY_TEAMS.length);
    const first = FIRST[Math.floor(rand(i * 6.7 + 5) * FIRST.length)];
    const last = LAST[Math.floor(rand(i * 8.1 + 6) * LAST.length)];
    list.push({
      id: `prospect-${i}`,
      name: `${first} ${last}`,
      position: POSITIONS[Math.floor(rand(i * 3.3 + 7) * POSITIONS.length)],
      org: ORGS[Math.floor(rand(i * 2.1 + 8) * ORGS.length)],
      level: LEVELS[Math.floor(rand(i * 9.4 + 9) * LEVELS.length)],
      age: 18 + Math.floor(rand(i * 7.2 + 10) * 6),
      eta: 2026 + Math.floor(rand(i * 4.8 + 11) * 4),
      rankings: { 'MLB.com': mlb, 'Baseball America': ba, 'Baseball Prospectus': bp },
      avgRank,
      fantasyTeamId: `team-${teamIdx + 1}`,
      fantasyTeamName: FANTASY_TEAMS[teamIdx],
    });
  }
  // Order by average rank and assign the overall #, level, and a MiLB stat line.
  list.sort((a, b) => a.avgRank - b.avgRank);
  list.forEach((p, idx) => {
    p.overall = idx + 1;
    p.avgRank = Math.round(p.avgRank);
    p.isPitcher = ['RHP', 'LHP'].includes(p.position);
    const rows = getSampleMinorLeagueStats({ id: p.id, isPitcher: p.isPitcher, org: p.org });
    const tier = p.overall <= 20 ? 0 : p.overall <= 40 ? 1 : 2; // AAA / AA / A+
    p.milb = rows[tier];
    p.level = p.milb.level;
    // Per-time-period stat lines for the Farm board's period filter.
    p.periodStats = {};
    FARM_PERIODS.forEach((per) => {
      const k = `${p.id}-${per.id}`;
      p.periodStats[per.id] = p.isPitcher ? genPitcherLine(p.milb, per, k, p.id) : genHitterLine(p.milb, per, k);
    });
    // Ownership: top prospects are mostly rostered; the deeper pool skews to free
    // agents so the "Available" view has plenty to browse. A null team = FA.
    const ownedProb = p.overall <= 15 ? 0.85 : p.overall <= 30 ? 0.6 : p.overall <= 45 ? 0.38 : 0.2;
    if (rand(idx * 11.7 + 40) >= ownedProb) {
      p.fantasyTeamId = null;
      p.fantasyTeamName = null;
    }
  });
  return list;
}

export function getSampleProspects() {
  if (!POOL) POOL = buildPool();
  return POOL;
}

export function getMyProspects(teamId = 'team-1') {
  return getSampleProspects().filter((p) => p.fantasyTeamId === teamId);
}

/**
 * Cumulative farm-system ranking per team: each prospect is worth (61 - overall)
 * points, so higher-ranked prospects (and more of them) lift a team's score.
 * Returns teams sorted best-first with count and a top-30 tally.
 */
export function getTeamProspectRankings() {
  const byTeam = {};
  FANTASY_TEAMS.forEach((name, i) => {
    byTeam[`team-${i + 1}`] = { teamId: `team-${i + 1}`, teamName: name, count: 0, top30: 0, score: 0 };
  });
  getSampleProspects().forEach((p) => {
    const t = byTeam[p.fantasyTeamId];
    if (!t) return;
    t.count += 1;
    if (p.overall <= 30) t.top30 += 1;
    t.score += Math.max(0, 61 - p.overall);
  });
  return Object.values(byTeam)
    .sort((a, b) => b.score - a.score)
    .map((t, i) => ({ ...t, rank: i + 1 }));
}
